import {
  EnvironmentProviders,
  InjectionToken,
  NgZone,
  inject,
  makeEnvironmentProviders,
} from '@angular/core';
import { FirebaseApp, getApps, initializeApp } from 'firebase/app';
import { Auth, User, connectAuthEmulator, getAuth } from 'firebase/auth';
import { Observable } from 'rxjs';
import { authState$ } from './utils/AuthRx';
import {
  Firestore,
  connectFirestoreEmulator,
  getFirestore,
} from 'firebase/firestore';
import { environment } from '../environments/environment';

/**
 * Firebase wiring, without @angular/fire.
 *
 * AngularFire has no Angular 22 build (latest stable 20.0.1 peers @angular/core ^20) and
 * only a release candidate for 21, so it pinned the whole app to Angular 20. It was also
 * the source of the two workarounds this replaces: NG0203 from AngularFirestore calling
 * inject() at construction time, and the compat/modular split where only one of the two
 * ever reached the emulators.
 *
 * Tokens rather than the SDK types as tokens: `Auth` and `FirebaseApp` are interfaces in
 * the modular SDK, so only `Firestore` could have doubled as its own token, and one of
 * the three behaving differently is worse than naming all three.
 *
 * Zone re-entry is unaffected and still required. Firestore and Auth deliver through
 * their own callbacks rather than through Angular, so the streams are built by
 * utils/FirestoreRx and utils/AuthRx, which re-enter at that seam.
 */
export const FIRESTORE = new InjectionToken<Firestore>('firebase.firestore');
export const FIREBASE_AUTH = new InjectionToken<Auth>('firebase.auth');

/**
 * The resolved auth state as a stream.
 *
 * Injected instead of `Auth` wherever a consumer only wants to know who is signed in:
 * the guards and AuthService. The modular SDK's functions runtime-check their first
 * argument, so a consumer that takes `Auth` can only be tested against a real Firebase
 * instance, while one that takes this can be handed `of(user)`. It is also the honest
 * dependency -- a guard needs the answer, not the SDK.
 */
export const AUTH_STATE = new InjectionToken<Observable<User | null>>(
  'firebase.authState',
);

export function provideFirebase(): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: FIRESTORE, useFactory: firestore },
    { provide: FIREBASE_AUTH, useFactory: auth },
    {
      provide: AUTH_STATE,
      useFactory: () => authState$(inject(FIREBASE_AUTH), inject(NgZone)),
    },
  ]);
}

/** Idempotent: karma reuses the page across specs, and initializeApp throws on a repeat. */
function app(): FirebaseApp {
  return getApps()[0] ?? initializeApp(environment.firebase);
}

function firestore(): Firestore {
  const db = getFirestore(app());
  if (environment.useEmulators) {
    connectFirestoreEmulator(db, 'localhost', 8000);
  }
  return db;
}

function auth(): Auth {
  const instance = getAuth(app());
  if (environment.useEmulators) {
    connectAuthEmulator(instance, 'http://localhost:9099', {
      disableWarnings: true,
    });
  }
  return instance;
}
